import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { canManageCourierCenters } from "../../utils/helper";
import CourierListPage, { CourierListHeadAction } from "./courier-list-page";
import CreateCenterForm from "./forms/create-center-form";
import EditCenterForm from "./forms/edit-center-form";
import Confirmation from "../commons/confirmation";
import CourierTable from "./ui/courier-table";
import CourierBooleanTag from "./ui/courier-boolean-tag";
import RowActions from "./ui/row-actions";
import { useCourierMutationDone } from "./ui/use-courier-mutation-done";
import { getCourierCenterTypesAPI } from "../../api/courier";
import { getCollectionCentersAPI } from "../../api/location";

const CentersList = props => {
  const {
    getCourierCenters,
    centers = [],
    courierLoading,
    createCourierCenter,
    updateCourierCenter,
    deleteCourierCenter,
    reorderCourierZoneCenters,
    courierApiError,
    resetCourierState,
    getCourierZones,
    zones = []
  } = props;

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [centerTypes, setCenterTypes] = useState([]);
  const [collectionCenters, setCollectionCenters] = useState([]);
  const [modalZoneId, setModalZoneId] = useState("");
  const canManage = canManageCourierCenters();

  useCourierMutationDone(courierLoading, courierApiError, () => {
    setIsOpenCreateModal(false);
    setEditTarget(null);
  });

  const orderedCenters = useMemo(
    () =>
      [...centers].sort((a, b) => {
        const zoneCmp = (a.zone ?? 0) - (b.zone ?? 0);
        if (zoneCmp !== 0) return zoneCmp;
        return (
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          a.name.localeCompare(b.name)
        );
      }),
    [centers]
  );

  useEffect(() => {
    getCourierCenters({});
    getCourierZones && getCourierZones({});
    getCourierCenterTypesAPI({ isActive: true }).then(resp => {
      if (resp?.data) {
        const list = Array.isArray(resp.data)
          ? resp.data
          : resp.data.results || [];
        setCenterTypes(list);
      }
    });
    return () => {
      resetCourierState();
    };
  }, []);

  useEffect(() => {
    const zone = zones.find(z => String(z.id) === String(modalZoneId));
    if (!zone?.region) {
      setCollectionCenters([]);
      return;
    }
    getCollectionCentersAPI({ regionId: zone.region }).then(resp => {
      if (resp?.data) {
        setCollectionCenters(Array.isArray(resp.data) ? resp.data : []);
      }
    });
  }, [modalZoneId, zones]);

  const moveCenter = (center, direction) => {
    const zoneCenters = orderedCenters.filter(c => c.zone === center.zone);
    const index = zoneCenters.findIndex(c => c.id === center.id);
    const target = index + direction;
    if (target < 0 || target >= zoneCenters.length) return;
    const ids = zoneCenters.map(c => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderCourierZoneCenters({ zoneId: center.zone, centerIds: ids });
  };

  return (
    <CourierListPage
      title="Courier Centers"
      count={centers.length}
      loading={courierLoading}
      headActions={
        canManage && (
          <CourierListHeadAction
            label="Add Center"
            onClick={() => {
              setModalZoneId("");
              setIsOpenCreateModal(true);
            }}
          />
        )
      }
    >
      <CourierTable>
        <thead>
          <tr>
            <th className="seq">Seq</th>
            <th>Name</th>
            <th>Code</th>
            <th>Type</th>
            <th>Zone</th>
            <th>Collection Center</th>
            <th>Vertical</th>
            <th>Contact</th>
            <th className="status">Active</th>
            {canManage && <th className="act">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {orderedCenters.map(c => {
            const zoneCenters = orderedCenters.filter(x => x.zone === c.zone);
            const indexInZone = zoneCenters.findIndex(x => x.id === c.id);
            return (
              <tr key={c.id}>
                <td className="seq">
                  {c.sortOrder ?? indexInZone + 1}
                  {canManage && (
                    <>
                      <button
                        type="button"
                        className="courier-move"
                        disabled={indexInZone === 0}
                        onClick={() => moveCenter(c, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="courier-move"
                        disabled={indexInZone === zoneCenters.length - 1}
                        onClick={() => moveCenter(c, 1)}
                      >
                        ↓
                      </button>
                    </>
                  )}
                </td>
                <td>
                  {c.name}
                  <span className="sm-text">ID {c.id}</span>
                </td>
                <td>{c.code || "—"}</td>
                <td>{c.centerTypeName}</td>
                <td>{c.zoneName}</td>
                <td>{c.collectionCenterName || "—"}</td>
                <td>{c.regionVerticalName || "—"}</td>
                <td>
                  {c.contactPerson}
                  {c.contactNumber ? ` (${c.contactNumber})` : ""}
                </td>
                <td className="status">
                  <CourierBooleanTag value={c.isActive} />
                </td>
                {canManage && (
                  <td className="act">
                    <RowActions
                      onEdit={() => setEditTarget(c)}
                      onDelete={() => setDeleteTarget(c)}
                    />
                  </td>
                )}
              </tr>
            );
          })}
          {!centers.length && !courierLoading && (
            <tr>
              <td colSpan={canManage ? 10 : 9}>No centers found.</td>
            </tr>
          )}
        </tbody>
      </CourierTable>

      {isOpenCreateModal && (
        <Modal
          isOpen={isOpenCreateModal}
          onRequestClose={() => setIsOpenCreateModal(false)}
          className="small-modal"
        >
          <CreateCenterForm
            closeModal={() => setIsOpenCreateModal(false)}
            loading={courierLoading}
            zones={zones}
            centerTypes={centerTypes}
            collectionCenters={collectionCenters}
            onZoneChange={setModalZoneId}
            apiError={courierApiError}
            createCourierCenter={createCourierCenter}
          />
        </Modal>
      )}

      {editTarget && (
        <Modal
          isOpen={!!editTarget}
          onRequestClose={() => setEditTarget(null)}
          className="small-modal"
        >
          <EditCenterForm
            center={editTarget}
            zones={zones}
            centerTypes={centerTypes}
            closeModal={() => setEditTarget(null)}
            loading={courierLoading}
            apiError={courierApiError}
            updateCourierCenter={updateCourierCenter}
          />
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          onRequestClose={() => setDeleteTarget(null)}
          className="small-modal"
        >
          <Confirmation
            text={`Delete center "${deleteTarget.name}"?`}
            onConfirm={() => {
              deleteCourierCenter(deleteTarget.id);
              setDeleteTarget(null);
            }}
            onClose={() => setDeleteTarget(null)}
          />
        </Modal>
      )}
    </CourierListPage>
  );
};

export default CentersList;
